import type { ModuleCode } from '../api/types';

// =============================================================================
// Slice 6c: Module-input mapping for hover-to-illuminate.
//
// When the user hovers over a module's risk column header (e.g. "Timeline"),
// the AssetGrid columns that feed that module's score get a subtle highlight,
// and a popover lists the parameters that ALSO feed the module but live
// in the asset deep-edit form rather than the grid.
//
// The mapping reflects the seeded metadata in V007–V016. It's hand-curated
// to match what the engine actually uses — not derived from the database
// at runtime, because pulling per-module parameter lists on every render
// would add latency and the seed metadata is stable.
//
// If a customer customizes their parameter set in Slice 9+, this mapping
// becomes potentially stale and we'd need to query the parameter→module
// linkage table at project-load time and pass it through to the grid.
// A later refactor.
//
// "Visible columns" are the AssetGrid column accessor keys that map to the
// module's input parameters. "Hidden inputs" are parameters that feed the
// module but aren't in the grid — listed in a popover so users see the
// complete picture without us having to widen the grid to 30 columns.
// =============================================================================

export interface ModuleInputs {
  /** Column accessor keys in the AssetGrid that feed this module. */
  visibleColumns: string[];
  /**
   * Module input parameters that feed this module's score but don't have
   * a corresponding visible column in the grid. Shown in the
   * hover popover so users get a complete picture.
   */
  hiddenInputs: string[];
}

export const MODULE_INPUTS: Record<ModuleCode, ModuleInputs> = {
  timeline: {
    visibleColumns: ['business_criticality', 'external_exposure'],
    hiddenInputs: [
      'expected_shelf_life',
      'time_for_migration',
      'crypto_agility',
      'upgradability',
    ],
  },
  data_risk: {
    visibleColumns: ['business_criticality', 'external_exposure'],
    hiddenInputs: [
      'data_part_of',
      'sndl_exposure',
      'regulatory_level',
    ],
  },
  supply_chain: {
    // The grid shows the vendor flag; the rest live in type_attributes
    // and are accessed in the deep-edit form.
    visibleColumns: ['vendor_supplied_asset', 'business_criticality'],
    hiddenInputs: [
      'vendor_readiness',
      'active_engagement',
      'alternative_sourcing_difficulty',
      'procurement_timelines_cycles',
      'source_country_risk',
    ],
  },
  migration_feasibility: {
    visibleColumns: ['business_criticality', 'asset_type_name'],
    hiddenInputs: [
      'crypto_agility',
      'upgradability',
      'legacy_system',
      'hardware_resource_constraints',
      'certification_requirement',
      'source_code_availability',
      'migration_cost',
      'complexity',
      'feasibility',
    ],
  },
  // Integrated Foundation is a composite — it doesn't take new inputs;
  // it aggregates the four Foundation module scores. So the "inputs"
  // are the four Foundation module score columns themselves.
  integrated_foundation: {
    visibleColumns: [
      'risk_timeline', 'risk_data_risk', 'risk_supply_chain',
      'risk_migration_feasibility',
    ],
    hiddenInputs: [],
  },
  // Slice 7: Threat module. Inputs come from threat_composition rows
  // matched to the asset's asset_type_id, NOT from per-asset parameters.
  // visibleColumns lists asset_type_name because that's the binding key —
  // hovering Threat illuminates the column that determines which threat
  // compositions apply to each asset.
  threat: {
    visibleColumns: ['asset_type_name'],
    hiddenInputs: [
      'matched_compositions',
      'composition_count',
      'severity_distribution',
      'impact_distribution',
    ],
  },
  cryptography: { visibleColumns: [], hiddenInputs: [] },
  compliance: { visibleColumns: [], hiddenInputs: [] },
  integrated_advanced: { visibleColumns: [], hiddenInputs: [] },
};

/** Foundation modules in display order (inputs left, integrated right). */
export const FOUNDATION_INPUT_MODULES: ModuleCode[] = [
  'timeline', 'data_risk', 'supply_chain', 'migration_feasibility',
];

/** Modules that aggregate other module scores. */
export const INTEGRATED_MODULES: ModuleCode[] = [
  'integrated_foundation', 'integrated_advanced',
];

/**
 * Convert a parameter key to a human-readable label for the popover.
 */
export function parameterLabel(key: string): string {
  const overrides: Record<string, string> = {
    expected_shelf_life: 'Expected shelf life',
    time_for_migration: 'Time for migration',
    crypto_agility: 'Crypto agility',
    upgradability: 'Upgradability',
    data_part_of: 'Data classification',
    sndl_exposure: 'Harvest-now-decrypt-later (HNDL) exposure',
    regulatory_level: 'Regulatory level',
    vendor_readiness: 'Vendor readiness',
    active_engagement: 'Active engagement',
    alternative_sourcing_difficulty: 'Alternative sourcing difficulty',
    procurement_timelines_cycles: 'Procurement timelines',
    source_country_risk: 'Source country risk',
    legacy_system: 'Legacy asset',
    hardware_resource_constraints: 'Hardware constraints',
    certification_requirement: 'Certification requirement',
    source_code_availability: 'Source code availability',
    migration_cost: 'Migration cost',
    complexity: 'Migration complexity',
    feasibility: 'Migration feasibility',
    matched_compositions: 'Matched threat compositions',
    composition_count: 'Composition count',
    severity_distribution: 'Vulnerability severity distribution',
    impact_distribution: 'Impact level distribution',
  };
  return overrides[key] ?? key.replace(/_/g, ' ');
}
